export default function About() {
  const sectionStyle = {
    maxWidth: "720px",
    marginLeft: "auto",
    marginRight: "auto",
    marginBottom: "40px",
  };

  const featureGridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "16px",
    marginTop: "20px",
  };

  const featureCardStyle = {
    background: "#111",
    border: "1px solid #444",
    borderRadius: "8px",
    padding: "16px",
    textAlign: "left",
  };

  const badgeStyle = {
    display: "inline-block",
    background: "#ffd600",
    color: "#000",
    fontWeight: "bold",
    fontSize: "0.75rem",
    borderRadius: "4px",
    padding: "2px 8px",
    marginBottom: "8px",
    letterSpacing: "0.5px",
  };

  const storeButtonStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "10px 20px",
    borderRadius: "8px",
    border: "1px solid #fff",
    background: "#1a1a1a",
    color: "#fff",
    fontSize: "0.95rem",
    fontWeight: "600",
    cursor: "pointer",
    textDecoration: "none",
    margin: "6px",
  };

  const dividerStyle = {
    border: "none",
    borderTop: "1px solid #333",
    margin: "40px auto",
    maxWidth: "720px",
  };

  const analyzerFeatures = [
    { icon: "🃏", title: "20-Card Format", desc: "Built specifically for 20-card keno — not a generic simulator." },
    { icon: "📋", title: "Custom Paytables", desc: "Select and fully customize paytables to match your machine." },
    { icon: "📊", title: "Session Analysis", desc: "Track hits, payouts, return%, EV%, win delta, and more per card." },
    { icon: "⚡", title: "Simulation Speed", desc: "Run single games or multi-game sessions at adjustable speeds." },
    { icon: "💾", title: "Save & Load", desc: "Save and reload configurations to compare strategies across sessions." },
    { icon: "🎯", title: "Spot Filtering", desc: "Filter analysis by spot size to zero in on what is working." },
  ];

  const optimizerFeatures = [
    { icon: "🔢", title: "Smart Optimization", desc: "Evaluates thousands of card configurations in seconds and generates evenly spread, Optimized cards." },
    { icon: "📈", title: "EV Optimization", desc: "Maximizes coverage within your picks ensuring Optimimum theoretical return when your block hits." },
    { icon: "💾", title: "Save & Load", desc: "Save and reload configurations and favorite patterns." },
    { icon: "📱", title: "Mobile App", desc: "Standalone app displays your optimized 20-card layout for easy transfer to a live machine.  Designed for quick, simple, real-world use." },
  ];

  const differentiators = [
    "Built from the ground up for 20-card keno — not ported from a generic odds calculator.",
    "Models real paytables accurately, including per-spot payout tiers.",
    "Tracks and analyzes actual session performance, not just theoretical odds.",
    "Includes an optimizer that finds the mathematically best configuration for your situation.",
    "Does not predict outcomes, promise \"hot\" numbers, or use any pseudoscience.",
    "Honest about the house edge — the goal is to make informed decisions, not to beat a game that cannot be beaten.",
  ];

  return (
    <div
      style={{
        maxWidth: "900px",
        margin: "40px auto",
        padding: "20px 20px 40px",
        color: "white",
        backgroundColor: "black",
        borderWidth: "1px",
        borderStyle: "solid",
        borderColor: "white",
        borderRadius: "8px",
      }}
    >
      {/* Page title */}
      <div style={{ textAlign: "center", marginBottom: "36px" }}>
        <h1 style={{ fontSize: "2.2rem", color: "#ffd600", marginBottom: "6px" }}>
          About Keno Analyzer
        </h1>
        <p style={{ fontSize: "1.1rem", color: "#ccc", margin: 0 }}>
          Built by a keno player, for keno players.
        </p>
      </div>

      {/* THE STORY */}
      <div style={sectionStyle}>
        <h2 style={{ fontSize: "1.5rem", color: "#ffd600", marginBottom: "16px" }}>
          Why This Exists
        </h2>
        <p style={{ fontSize: "1.05rem", lineHeight: "1.75", color: "#ddd" }}>
          I have been playing keno for a long time — 2-spots, 5-spots, 8-spots, every combination
          of spot sizes and credit bets imaginable. Clustering numbers, spreading them out,
          favorite numbers, quick picks. I have tried it all.
        </p>
        <p style={{ fontSize: "1.05rem", lineHeight: "1.75", color: "#ddd" }}>
          Sure, big wins happen — but they are few and far between. I wanted to understand
          <em> why</em> nothing consistently worked. That led me down the rabbit hole of house
          edge, expected value, and the brutal mathematics of keno.
        </p>
        <p style={{ fontSize: "1.05rem", lineHeight: "1.75", color: "#ddd" }}>
          The answer starts with the house edge. Casinos know that hitting 7-of-7 is a roughly
          49,000:1 shot — but they pay only 7,000:1. They do not care what numbers you play,
          what time of day it is, or how lucky you feel. Over years of play it all averages
          out — for them.
        </p>
        <p style={{ fontSize: "1.05rem", lineHeight: "1.75", color: "#ddd" }}>
          But there is more to it. You also get paid for partial hits — 6-of-7, 7-of-8, and so on.
          Those smaller, more frequent wins are what keep your bankroll alive long enough
          for the bigger hits to come in. <strong style={{ color: "#fff" }}>That is where strategy lives.</strong>
        </p>
        <p style={{ fontSize: "1.05rem", lineHeight: "1.75", color: "#ddd" }}>
          With 20 active cards, multiple bet options per card, and a range of spot sizes,
          there are literally thousands of possible combinations. If you play 4 four-spot cards
          at 4 credits, 6 five-spot cards at 3 credits, 4 seven-spots at 2 credits, and 3
          eight-spots at 1 credit — then run 100 games — which combination returned more than
          it cost? Which card carried the session, and which dragged it down?
        </p>
        <p style={{ fontSize: "1.05rem", lineHeight: "1.75", color: "#ddd" }}>
          I searched for a tool to answer those questions. Everything I found was either a
          copy of the game itself, or a "hot and cold numbers" gimmick that was essentially
          useless. So I built exactly what I needed.
        </p>
      </div>

      <hr style={dividerStyle} />

      {/* KENO ANALYZER */}
      <div style={sectionStyle}>
        <h2 style={{ fontSize: "1.5rem", color: "#ffd600", marginBottom: "8px" }}>
          The Keno Analyzer
        </h2>
        <p style={{ fontSize: "1.05rem", lineHeight: "1.75", color: "#ddd", marginBottom: "6px" }}>
          A purpose-built simulator and session tracker for 20-card keno. It models real
          casino play — with real paytables, real session behavior, and analysis that
          reflects how players actually play.
        </p>

        <div
          style={{
            width: "100%",
            background: "transparent",
            border: "0px dashed #444",
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#555",
            fontSize: "0.9rem",
            marginTop: "20px",
            marginBottom:"-20px",
            minHeight: "180px",
            paddingBottom: "0"
          }}
        >
      <img
        src={process.env.PUBLIC_URL + "/keno-analyzer-full-screen-001.png"}
        alt="Keno Analyzer Screenshot"
        style={{
          width: "100%",
          maxWidth: "760px",
          height: "auto",
          borderRadius: "8px",
          border: "3px solid #444",
          display: "block",
          margin: "0 auto 20px",
        }}
      />
       </div>
         

        <div style={featureGridStyle}>
          {analyzerFeatures.map(f => (
            <div key={f.title} style={featureCardStyle}>
              <div style={{ fontSize: "1.5rem", marginBottom: "6px" }}>{f.icon}</div>
              <div style={{ fontWeight: "700", color: "#fff", marginBottom: "4px" }}>{f.title}</div>
              <div style={{ fontSize: "0.9rem", color: "#aaa", lineHeight: "1.5" }}>{f.desc}</div>
            </div>
          ))}
        </div>
      </div>

      <hr style={dividerStyle} />

      {/* KENO OPTIMIZER */}
      <div style={sectionStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px" }}>
          <h2 style={{ fontSize: "1.5rem", color: "#ffd600", margin: 0 }}>
            The Keno Optimizer
          </h2>
          <span style={badgeStyle}>NEW</span>
        </div>

      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "12px", marginBottom: "8px", border: "0px solid #444", padding: "0px", paddingTop: "0px",borderRadius: "8px" }}>
        <p style={{ fontSize: "1.05rem", lineHeight: "1.75", color: "#ddd", marginBottom: "6px" }}>
          The Keno Optimizer takes your analysis to the next level. When active, it allows you to
          select up to 20 numbers, either clustered in a group, or spread out however you like, 
          and then mathematically searches thousands of card configurations and automatically
          populates your cards with the spot size you've chosen ensuring optimum spread and 
          even distribution of your chosen numbers.
        </p>
        <p style={{ fontSize: "1.05rem", lineHeight: "1.75", color: "#ddd" }}>
          The Keno Optimizer is a fully functional 
          <strong style={{ color: "#fff" }}> standalone mobile app</strong> designed for real-world use
          right in the casino.  Select your numbers, click 'Optimize', 
          and the app displays your optimized 20-card layout
          in a scrollable view — so you can easily transfer the configuration to a live machine.
        </p>
      </div>


        <div
          style={{
            width: "100%",
            background: "transparent",
            border: "0px dashed #444",
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#555",
            fontSize: "0.9rem",
            margin: "20px 0",
            marginBottom: "-20px",
            minHeight: "180px",
          }}
        >
           <img
        src={process.env.PUBLIC_URL + "/keno-analyzer-optimizer-mode-001.png"}
        alt="Keno Analyzer Screenshot"
        style={{
          width: "80%",
          maxWidth: "760px",
          height: "auto",
          borderRadius: "8px",
          border: "3px solid #444",
          display: "block",
          margin: "0 auto 40px",
        }}
      />

        </div>


        <div style={featureGridStyle}>
          {optimizerFeatures.map(f => (
            <div key={f.title} style={featureCardStyle}>
              <div style={{ fontSize: "1.5rem", marginBottom: "6px" }}>{f.icon}</div>
              <div style={{ fontWeight: "700", color: "#fff", marginBottom: "4px" }}>{f.title}</div>
              <div style={{ fontSize: "0.9rem", color: "#aaa", lineHeight: "1.5" }}>{f.desc}</div>
            </div>
          ))}
        </div>

        <div style={{ textAlign: "center", marginTop: "28px" }}>
          <a
            href="https://kenooptimizer.com"
            target="_blank"
            rel="noreferrer"
            style={{ display: "inline-block", color: "#ffd600", fontWeight: "700", fontSize: "1.05rem", marginBottom: "14px", textDecoration: "none" }}
          >
            kenooptimizer.com ↗
          </a>
          <p style={{ color: "#aaa", marginBottom: "14px", fontSize: "0.95rem" }}>
            Mobile app currently in closed testing — coming soon:
          </p>
          <button type="button" style={storeButtonStyle}>
            <span style={{ fontSize: "1.3rem" }}>🍎</span> TestFlight (iOS)
          </button>
          <button type="button" style={storeButtonStyle}>
            <span style={{ fontSize: "1.3rem" }}>▶</span> Google Play (Beta)
          </button>
        </div>
      </div>

      <hr style={dividerStyle} />

      {/* WHAT MAKES IT DIFFERENT */}
      <div style={sectionStyle}>
        <h2 style={{ fontSize: "1.5rem", color: "#ffd600", marginBottom: "16px" }}>
          What Makes It Different
        </h2>
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "12px" }}>
          {differentiators.map((item, i) => (
            <li key={i} style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
              <span style={{ color: "#ffd600", fontWeight: "bold", flexShrink: 0 }}>✓</span>
              <span style={{ fontSize: "1.0rem", color: "#ddd", lineHeight: "1.6" }}>{item}</span>
            </li>
          ))}
        </ul>
      </div>

      <hr style={dividerStyle} />

      {/* CTA */}
      <div style={{ textAlign: "center", marginTop: "10px" }}>
        <p style={{ fontSize: "1.1rem", color: "#ccc", marginBottom: "20px" }}>
          Ready to understand your keno play like never before?
        </p>
        <button
          className="home-cta-btn join"
          style={{ marginRight: "12px" }}
          onClick={() => (window.location.href = "/join")}
        >
          Join Free
        </button>
        <button
          className="home-cta-btn login"
          onClick={() => (window.location.href = "/login")}
        >
          Log In
        </button>
      </div>
    </div>
  );
}
